import {
  findDeletedImage,
  partitionFileObjects,
  PartitionFileObjectsType,
} from '@utils';
import { FileObject } from '@hooks';
import * as Sentry from '@sentry/react';
import React from 'react';
import {
  ErrorMessages,
  ServiceLocationDetailsObjectType,
  emptyServiceLocationDetails,
} from '@types';
import { UseMutateFunction } from '@tanstack/react-query';

interface Props {
  files: File[];
  setProcessingPhoto: React.Dispatch<React.SetStateAction<boolean>>;
  uploadedFiles: FileObject[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<FileObject[]>>;
  setErrorMessages: React.Dispatch<React.SetStateAction<ErrorMessages[]>>;
  updateUploadedImages: (uploadedImages: PartitionFileObjectsType) => void;
  updateIsPhotoLocationStored: (isPhotoLocationStored: boolean) => void;
  errorPath: string;
  fileWithLoadedCoordinates: string;
  updateFileWithLoadedCoordinates: (fileWithLoadedCoordinates: string) => void;
  updateServiceLocationDetails: (
    serviceLocationDetails: ServiceLocationDetailsObjectType,
  ) => void;
  mutateDelete: UseMutateFunction<void, Error, string, unknown>;
}

export const handleFileDelete = ({
  files,
  setProcessingPhoto,
  uploadedFiles,
  setUploadedFiles,
  setErrorMessages,
  updateUploadedImages,
  updateIsPhotoLocationStored,
  fileWithLoadedCoordinates,
  updateFileWithLoadedCoordinates,
  updateServiceLocationDetails,
  mutateDelete,
  errorPath,
}: Props) => {
  setProcessingPhoto(true);
  const deletedFileObject = findDeletedImage(uploadedFiles, files);
  let remainingFiles: FileObject[];
  if (deletedFileObject) {
    if (deletedFileObject.file.name === fileWithLoadedCoordinates) {
      updateServiceLocationDetails(emptyServiceLocationDetails);
      updateIsPhotoLocationStored(false);
      updateFileWithLoadedCoordinates('');
    }
    if (deletedFileObject.blob_endpoint) {
      remainingFiles = uploadedFiles.filter(
        (file) => file.blob_endpoint !== deletedFileObject.blob_endpoint,
      );
      try {
        mutateDelete(deletedFileObject.blob_endpoint);
        setUploadedFiles(remainingFiles);
        partitionFileObjects(remainingFiles).then((partitionedFiles) => {
          updateUploadedImages(partitionedFiles);
        });
        setErrorMessages([]);
      } catch (error) {
        Sentry.captureException(error);
        setErrorMessages([
          { path: errorPath, message: (error as Error).message },
        ]);
      } finally {
        setProcessingPhoto(false);
      }
    } else {
      remainingFiles = uploadedFiles.filter(
        (file) => file.file.name !== deletedFileObject.file.name,
      );
      setUploadedFiles(remainingFiles);
      partitionFileObjects(remainingFiles).then((partitionedFiles) => {
        updateUploadedImages(partitionedFiles);
      });
      setErrorMessages([]);
    }
  } else {
    setErrorMessages([
      {
        path: errorPath,
        message: 'Failed to delete image. Please try again',
      },
    ]);
  }
  setProcessingPhoto(false);
};
